/**
 * Default `BrandingTokens` fixture for the public-website SPA.
 *
 * **Placeholder until `@dvgup/branding` lands** — TODO-013 (brand assets)
 * is DEFERRED per `dvgup-platform/requirements.md` §16, so the workspace
 * package `@dvgup/branding` (`packages/branding/`) that owns the runtime
 * token bundle has not yet scaffolded. Until then, the SPA's
 * `<ThemeProvider>` boundary needs *some* concrete `BrandingTokens` value
 * to construct an MUI theme; this file supplies one so `App.tsx` can call
 * `createAppTheme(brandingFixture)` without import errors.
 *
 * **Color seed.** Color values below are pulled from the operator-supplied
 * Material 3 theme at `temp/material-theme.json` (Material Theme Builder
 * export, seed `#004545`, light scheme). The flat `colors.*` slot maps to
 * the closest Material 3 role:
 *
 *   - `colors.primary`    → `schemes.light.primary`     (`#006A6A`)
 *   - `colors.secondary`  → `schemes.light.secondary`   (`#4A6363`)
 *   - `colors.accent`     → `schemes.light.tertiary`    (`#4B607C`)
 *   - `colors.background` → `schemes.light.background`  (`#F4FBFA`)
 *   - `colors.text`       → `schemes.light.onBackground`(`#161D1D`)
 *
 * Dark-mode tokens are NOT carried in `BrandingTokens` v1 (the type
 * shape is single-mode); when `core-infrastructure` ships a v2
 * `BrandingTokens` with per-mode color tokens, `theme-from-branding.ts`
 * can plug the second scheme into MUI 6+'s `colorSchemes: { light, dark }`.
 *
 * **Asset URLs.** Logo / favicon / platform-icon / share-image URLs
 * point at the SPA's `/branding/light/...` tree, which Vite copies
 * verbatim from `websites/public-website/public/branding/` into
 * `dist/branding/` at build time. The deploy wrapper's `aws s3 sync`
 * carries the tree to the bucket with the long-cache header so the
 * assets serve via CloudFront on first byte.
 *
 * The shape mirrors `BrandingTokens` re-exported from
 * `@dvgup/contracts/branding-config` (see `models/src/branding-config.ts`).
 *
 * **Migration path.** When `@dvgup/branding` scaffolds, this file's
 * default export is replaced with `export { brandingTokens as
 * brandingFixture } from '@dvgup/branding';` and `App.tsx`'s import is
 * left untouched. No call-site changes downstream.
 */

import type { BrandingTokens } from '@dvgup/contracts/branding-config';

/**
 * Placeholder `BrandingTokens` value used by `App.tsx` to build the SPA's
 * MUI theme. Replaced by the real `@dvgup/branding` runtime export once
 * TODO-013 lands.
 */
export const brandingFixture: BrandingTokens = {
  // Color values seeded from `temp/material-theme.json::schemes.light`.
  // The Material 3 role-to-flat-token mapping is documented in the file
  // header above so the next iteration knows where to plug in the v2
  // per-mode shape from `core-infrastructure`'s follow-up.
  colors: {
    primary: '#006A6A',
    secondary: '#4A6363',
    accent: '#4B607C',
    background: '#F4FBFA',
    text: '#161D1D',
  },
  typography: {
    headingFont: 'Roboto, sans-serif',
    bodyFont: 'Roboto, sans-serif',
    monoFont: 'Roboto Mono, monospace',
  },
  // Logo / favicon URLs point at the SPA's `/branding/light/...` tree
  // (Vite copies `public/` verbatim into `dist/`). The dark variants
  // ship in `/branding/dark/...` so a future system-color-scheme
  // branch in the SPA can swap them at runtime.
  logos: {
    primary: '/branding/light/logo-primary.svg',
    mono: '/branding/light/logo-mono.svg',
    favicon: '/branding/light/favicon.svg',
  },
  copy: {
    tagline: 'Daily video-game updates.',
    podcastSubtitle: 'The DVGUP podcast.',
    newsletterFooter: 'You can unsubscribe at any time.',
    // Multi-paragraph placeholder for the `/about` route until
    // TODO-029 (public-website tagline + description) lands the
    // real long-form copy. Paragraphs are blank-line-separated so
    // `<AboutPage>` renders one `<Typography variant="body1">` per
    // paragraph (Requirement 2.7).
    about:
      'DVGUP is the daily video-game updates platform. Every day, we publish a Daily covering the day\u2019s releases, news, and a fact of the day across PlayStation, Xbox, Nintendo Switch, PC, Steam Deck, and mobile.\n\nThis page is a placeholder until TODO-029 lands the long-form description.',
  },
  copyright: '\u00A9 2026 DVGUP. All rights reserved.',
  placeholders: {
    // Substituted by `ReleasesManager` when an upstream release record
    // arrives without an `image` field per R6.3 / parent §12. Points
    // at the same `icon-primary.svg` the SPA's chrome uses so the
    // fallback is visually coherent until upstream feeds carry real
    // cover art.
    releaseImage: '/branding/light/icon-primary.svg',
  },
  platformIcons: {
    playstation: '/branding/light/platform-playstation.svg',
    xbox: '/branding/light/platform-xbox.svg',
    nintendoSwitch: '/branding/light/platform-nintendoSwitch.svg',
    pc: '/branding/light/platform-pc.svg',
    steamDeck: '/branding/light/platform-steamDeck.svg',
    mobile: '/branding/light/platform-mobile.svg',
    unknown: '/branding/light/platform-unknown.svg',
  },
  // Only the RSS feed URL ships in the placeholder fixture so the
  // public-website `/podcast` page (task 9.1) renders the "subscribe
  // link coming soon" branch for the six per-platform entries until
  // `@dvgup/branding` lands real subscribe URLs (TODO-013). The RSS
  // value is the canonical feed URL from
  // `BrandingConfig.podcastSubscribe.rssFeed` per Requirement 4.3.
  podcastSubscribe: {
    rssFeed: 'https://example.invalid/podcast.rss',
  },
  // Share / SEO metadata consumed by the SPA's `<SeoHead>` boundary
  // via `lib/seo/meta-builder.ts` (task 10.1). `defaultImage` now
  // points at the brand's social card SVG (TODO-027 unblocked);
  // `twitterHandle` (TODO-028) remains undefined until the brand
  // claims a handle.
  share: {
    tagline: 'Daily video-game updates.',
    description: 'DVGUP \u2014 daily video-game updates.',
    defaultImage: '/branding/light/social-card.svg',
    // twitterHandle: undefined \u2014 TODO-028 (Twitter handle for `twitter:site`)
  },
};
